/**
 * Edit Dietary Comments — opened from the Pullman Menu Cards tile, beside the
 * "Highlight dietary notes" checkbox, for the selected WHOLE service.
 *
 * One row per booking on the service: guest, carriage / table / seats, the
 * menu, MERAC's booking note, and the editable DIETARY COMMENT the card will
 * print in its banner. Save writes cache.MenuCardDietaryOverrides (db/86) —
 * separate from the nightly-truncated cache, so an edit persists between MERAC
 * updates; a booking without an edit keeps following MERAC. Clearing a
 * comment (Reset to MERAC, then Save) deletes the override.
 *
 * Rows with a saved edit are shaded amber and badged "Updated" (by whom, when);
 * an unsaved edit is shaded green. "Show all / Show updated only" filters.
 *
 * One owner of "what the card prints": the API layers the override onto the
 * booking (`dietary_override`); the template's dietarySummary reads it first.
 */
import { useEffect, useMemo, useState } from "react";
import type { DietaryOverrideOp, MenuCardBooking, MenuCardService } from "./types";
import { saveMenuCardDietaryOverrides } from "./useMenuCards";
import { cleanBookingNotes } from "./templates";

export interface DietaryCommentsDialogProps {
  open: boolean;
  service: MenuCardService | null;
  bookings: MenuCardBooking[];
  onClose: () => void;
  /** Called after a successful save so the cards behind refetch. */
  onSaved: () => void;
}

type Filter = "all" | "updated";

function keyOf(b: MenuCardBooking): string {
  return `${b.booking_reference}|${b.service_date}`;
}

/** What the card prints today: the saved edit, else MERAC's cleaned note. */
function printedToday(b: MenuCardBooking): string {
  const o = (b.dietary_override ?? "").trim();
  return o || cleanBookingNotes(b.booking_notes);
}

function whenLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

function whoLabel(upn: string | null): string {
  if (!upn) return "";
  const local = upn.split("@")[0];
  return local.split(/[._-]+/).filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ") || upn;
}

function shortCarriage(name: string): string {
  return name.replace(/^Pullman\s*-\s*/i, "").trim();
}

function menuLines(menus: string, max = 4): string {
  const lines = menus.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const shown = lines.slice(0, max);
  return shown.join("\n") + (lines.length > max ? `\n… ${lines.length - max} more` : "");
}

export function DietaryCommentsDialog({ open, service, bookings, onClose, onSaved }: DietaryCommentsDialogProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [resets, setResets] = useState<Set<string>>(() => new Set());
  const [filter, setFilter] = useState<Filter>("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Fresh drafts every time the dialog opens or the service's bookings change
  // (after a save the parent refetches; the new overrides become the baseline).
  useEffect(() => {
    if (!open) return;
    const d: Record<string, string> = {};
    for (const b of bookings) d[keyOf(b)] = printedToday(b);
    setDrafts(d);
    setResets(new Set());
    setError(null);
  }, [open, bookings]);

  useEffect(() => {
    if (open) { setFilter("all"); setNotice(null); }
  }, [open]);

  // Escape closes this dialog only, not the A La Carte modal behind it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  const isDirty = (b: MenuCardBooking): boolean => {
    const k = keyOf(b);
    if (resets.has(k)) return Boolean((b.dietary_override ?? "").trim());
    const draft = (drafts[k] ?? "").trim();
    return draft !== printedToday(b).trim();
  };

  const dirtyCount = useMemo(() => bookings.filter(isDirty).length, [bookings, drafts, resets]); // eslint-disable-line react-hooks/exhaustive-deps
  const updatedCount = useMemo(() => bookings.filter((b) => Boolean((b.dietary_override ?? "").trim())).length, [bookings]);

  const visible = useMemo(
    () => (filter === "updated" ? bookings.filter((b) => Boolean((b.dietary_override ?? "").trim()) || isDirty(b)) : bookings),
    [bookings, filter, drafts, resets] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!open) return null;

  const setDraft = (b: MenuCardBooking, v: string) => {
    const k = keyOf(b);
    setNotice(null);
    setDrafts((prev) => ({ ...prev, [k]: v }));
    setResets((prev) => { if (!prev.has(k)) return prev; const n = new Set(prev); n.delete(k); return n; });
  };

  const resetRow = (b: MenuCardBooking) => {
    const k = keyOf(b);
    setNotice(null);
    setDrafts((prev) => ({ ...prev, [k]: cleanBookingNotes(b.booking_notes) }));
    setResets((prev) => new Set(prev).add(k));
  };

  const onSave = async () => {
    const ops: DietaryOverrideOp[] = [];
    for (const b of bookings) {
      if (!isDirty(b)) continue;
      const k = keyOf(b);
      const text = resets.has(k) ? null : (drafts[k] ?? "").trim() || null;
      ops.push({ booking_reference: b.booking_reference, service_date: b.service_date, text });
    }
    if (ops.length === 0) { setError("Nothing has been changed yet."); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await saveMenuCardDietaryOverrides(ops);
      const parts: string[] = [];
      if (res.saved) parts.push(`${res.saved} comment${res.saved === 1 ? "" : "s"} saved`);
      if (res.cleared) parts.push(`${res.cleared} reset to MERAC`);
      setNotice(`${parts.join(", ") || "Saved"}. Changes here will persist between MERAC updates.`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const title = service
    ? `${service.service_day_label} · ${service.service_time_label.replace(/^\d{4}-\d{2}-\d{2}\s*/, "")} · ${service.journey_description}`
    : "";

  return (
    <div className="cl-custom-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cl-custom cl-editor diet-dialog" role="dialog" aria-modal="true" aria-labelledby="diet-dialog-title">
        <div className="cl-custom-head">
          <h3 id="diet-dialog-title">
            Edit Dietary Comments
            <small>{title}{bookings.length ? ` · ${bookings.length} booking${bookings.length === 1 ? "" : "s"}` : ""}</small>
          </h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="diet-toolbar">
          <div className="cl-editor-pills" role="radiogroup" aria-label="Show">
            <button
              type="button" role="radio" aria-checked={filter === "all"}
              className={"cl-editor-pill" + (filter === "all" ? " cl-editor-pill--active" : "")}
              onClick={() => setFilter("all")}
            >
              Show all ({bookings.length})
            </button>
            <button
              type="button" role="radio" aria-checked={filter === "updated"}
              className={"cl-editor-pill" + (filter === "updated" ? " cl-editor-pill--active" : "")}
              onClick={() => setFilter("updated")}
            >
              Show updated only ({updatedCount})
            </button>
          </div>
          <span className="diet-toolbar-hint">
            The comment prints in the card's DIETARY NOTES banner and overrides MERAC's booking note.
            Amber rows carry a saved edit; green rows have unsaved changes.
          </span>
        </div>

        <div className="diet-table-wrap">
          {bookings.length === 0 ? (
            <div className="diet-empty">No bookings on this service.</div>
          ) : visible.length === 0 ? (
            <div className="diet-empty">No booking on this service has an updated dietary comment yet.</div>
          ) : (
            <table className="diet-table">
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Guest</th>
                  <th>Carriage · table · seats</th>
                  <th>Menu card</th>
                  <th>MERAC dietary note</th>
                  <th>Dietary comment on the card</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((b) => {
                  const k = keyOf(b);
                  const updated = Boolean((b.dietary_override ?? "").trim());
                  const dirty = isDirty(b);
                  const merac = cleanBookingNotes(b.booking_notes);
                  return (
                    <tr key={k} className={dirty ? "diet-row--dirty" : updated ? "diet-row--updated" : undefined}>
                      <td>#{b.booking_reference}</td>
                      <td>
                        {b.account_name}
                        {b.footfall != null && <span className="diet-meta">{b.footfall} diner{b.footfall === 1 ? "" : "s"}</span>}
                      </td>
                      <td>{shortCarriage(b.carriage)} · Table {b.table_name} · {b.seats}</td>
                      <td className="diet-cell-menu">{menuLines(b.menus)}</td>
                      <td className="diet-cell-merac">
                        {merac || <em>— none —</em>}
                        {!merac && b.has_dietary_notes && <span className="diet-meta">Flagged by MERAC from the menu lines</span>}
                      </td>
                      <td>
                        <textarea
                          value={drafts[k] ?? ""}
                          maxLength={400}
                          placeholder="Nothing prints unless a comment is entered"
                          onChange={(e) => setDraft(b, e.target.value)}
                          aria-label={`Dietary comment for booking ${b.booking_reference}`}
                        />
                        <button type="button" className="diet-reset" onClick={() => resetRow(b)} disabled={!updated && !dirty}
                          title="Discard the edit and let the card follow MERAC's note again (takes effect on Save)">
                          Reset to MERAC
                        </button>
                      </td>
                      <td>
                        {dirty ? (
                          <span className="diet-badge diet-badge--dirty">{resets.has(k) ? "Reset pending" : "Edited, unsaved"}</span>
                        ) : updated ? (
                          <>
                            <span className="diet-badge">Updated</span>
                            <span className="diet-meta">
                              {whoLabel(b.dietary_updated_by)}{b.dietary_updated_at ? ` · ${whenLabel(b.dietary_updated_at)}` : ""}
                            </span>
                          </>
                        ) : (
                          <span className="diet-meta">Following MERAC</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="alacarte-footer cl-editor-footer">
          <div className="alacarte-footer-summary">
            {error
              ? <span className="alacarte-footer-error">{error}</span>
              : notice
                ? <span className="cl-editor-notice">{notice}</span>
                : dirtyCount > 0
                  ? <span>{dirtyCount} booking{dirtyCount === 1 ? "" : "s"} changed, not yet saved</span>
                  : <span className="alacarte-footer-hint">No changes yet</span>}
          </div>
          <div className="alacarte-footer-actions">
            <span className="diet-persist-note">Changes here will persist between MERAC updates</span>
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>Close</button>
            <button type="button" className="btn btn--primary" onClick={() => void onSave()} disabled={busy || dirtyCount === 0}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
